import { ColorMode } from "@intentsify/brand";
import { formatDate } from "@intentsify/utils";
import {
  barY,
  crosshair,
  defineChart,
  dot,
  group,
  lineY,
  ruleX,
  text,
} from "@tanstack/charts";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { scaleLog, scaleLinear as d3ScaleLinear } from "d3-scale";
import { DateTime } from "luxon";
import { AnyColour, Colour } from "theme";
import { BrandChartTheme, brandColourFor } from "../BrandChart";
import { TimeseriesDataItem, XAxisLabel } from "../Charts.types";
import { formatTickValue } from "../Charts.utils";
import {
  lineTypeToDashArray,
  MarkerLineType,
} from "./components/Markers.utils";
import { createGetColour } from "./Timeseries.hooks";
import { TimeseriesProps } from "./Timeseries.types";
import { generateTimeValuesSeq } from "./Timeseries.utils";

export type SeriesKind = "bar" | "line";
export type SeriesAxis = "left" | "right";

export type TimeseriesNewSeries = {
  index: number;
  kind: SeriesKind;
  axis: SeriesAxis;
  label: string;
  group?: string;
  colour: AnyColour;
  muted: boolean;
  data: TimeseriesDataItem[];
};

export type TimeseriesNewRow = {
  seriesIndex: number;
  kind: SeriesKind;
  axis: SeriesAxis;
  label: string;
  colour: AnyColour;
  isoDate: string;
  value: number;
};

export type MarkerRow = {
  isoDate: string;
  color?: string;
  strokeWidth: number;
  strokeDasharray?: string;
  description: React.ReactElement;
};

type MarkerLabelRow = { isoDate: string; name: string; color?: string };

/** Opacity for series flagged `muteColor`, matching the legacy 30% mix. */
export const MUTED_OPACITY = 0.35;

const toTime = (isoDate: string) =>
  DateTime.fromISO(isoDate, { zone: "utc" }).valueOf();

const byDate = (a: TimeseriesDataItem, b: TimeseriesDataItem) =>
  toTime(a.isoDate) - toTime(b.isoDate);

/**
 * Flattens the four series props into one list, sorted by date, with a
 * colour picked for any series that did not set one.
 */
export const toTimeseriesNewSeries = ({
  leftAxisBars = [],
  rightAxisBars = [],
  leftAxisLines = [],
  rightAxisLines = [],
}: Pick<
  TimeseriesProps,
  "leftAxisBars" | "rightAxisBars" | "leftAxisLines" | "rightAxisLines"
>): TimeseriesNewSeries[] => {
  const groups = [
    { items: leftAxisLines, kind: "line", axis: "left" },
    { items: rightAxisLines, kind: "line", axis: "right" },
    { items: leftAxisBars, kind: "bar", axis: "left" },
    { items: rightAxisBars, kind: "bar", axis: "right" },
  ] as const;

  const used = groups
    .flatMap(({ items }) => items.map((item) => item.legend.color))
    .filter((colour): colour is Colour => Boolean(colour));
  const nextColour = createGetColour(used);

  return groups
    .flatMap(({ items, kind, axis }) =>
      items.map((item) => ({ item, kind, axis })),
    )
    .map(({ item, kind, axis }, index) => ({
      index,
      kind,
      axis,
      label: item.legend.label,
      group: item.legend.group,
      colour: item.legend.color ?? nextColour(),
      muted: item.legend.muteColor ?? false,
      data: [...item.data].sort(byDate),
    }));
};

/** Sorted x positions; with a gap, missing steps between dates are added. */
export const getTimeValues = (
  series: TimeseriesNewSeries[],
  markerDates: string[],
  timeGap?: number,
) => {
  const dates = series
    .flatMap((item) => item.data.map((point) => point.isoDate))
    .concat(markerDates);

  if (typeof timeGap === "number") {
    return generateTimeValuesSeq(timeGap, dates);
  }

  return Array.from(new Set(dates)).sort((a, b) => toTime(a) - toTime(b));
};

/** Value domain for one axis; linear domains start at zero and are niced. */
export const getValueDomain = (
  values: number[],
  scale: "linear" | "log",
): [number, number] => {
  if (scale === "log") {
    const positive = values.filter((value) => value > 0);

    return positive.length
      ? [Math.min(...positive), Math.max(...positive)]
      : [1, 10];
  }

  const [min, max] = d3ScaleLinear()
    .domain([0, Math.max(0, ...values) || 1])
    .nice()
    .domain();

  return [min, max];
};

const isPowerOfTen = (value: number) =>
  value > 0 && Number.isInteger(Math.log10(value));

const valueAxis = (
  scale: "linear" | "log",
  label: string | undefined,
  skipDecimals: boolean,
) => ({
  line: false,
  label,
  ticks: {
    size: 0,
    format: (value: number) =>
      (scale === "log" && !isPowerOfTen(value)) ||
      (skipDecimals && !Number.isInteger(value))
        ? ""
        : formatTickValue(value),
  },
});

const valueScale = (domain: [number, number], scale: "linear" | "log") =>
  scale === "log" ? scaleLog().domain(domain) : scaleLinear().domain(domain);

export const formatTimeValue = (isoDate: string) =>
  formatDate({ date: DateTime.fromISO(isoDate).toJSDate() });

type DefinitionOptions = {
  series: TimeseriesNewSeries[];
  timeValues: string[];
  markers?: TimeseriesProps["markers"];
  xAxisLabels?: XAxisLabel[];
  brandTheme: BrandChartTheme;
  scale?: "linear" | "log";
  leftAxisLabel?: string;
  rightAxisLabel?: string;
  skipDecimals?: boolean;
  padding?: { paddingInner: number; paddingOuter: number };
  displayLinesInFrontOfBars?: boolean;
};

export const seriesColour = (
  series: Pick<TimeseriesNewSeries, "colour">,
  mode: ColorMode,
) => brandColourFor(series.colour, mode);

export const buildTimeseriesNewDefinition = ({
  series,
  timeValues,
  markers = [],
  xAxisLabels = [],
  brandTheme,
  scale = "linear",
  leftAxisLabel,
  rightAxisLabel,
  skipDecimals = false,
  padding = { paddingInner: 0.3, paddingOuter: 0.3 },
  displayLinesInFrontOfBars = false,
}: DefinitionOptions) => {
  const rowsOf = (item: TimeseriesNewSeries): TimeseriesNewRow[] =>
    item.data.map((point) => ({
      seriesIndex: item.index,
      kind: item.kind,
      axis: item.axis,
      label: item.label,
      colour: item.colour,
      isoDate: point.isoDate,
      value: point.value,
    }));

  const valuesOn = (axis: SeriesAxis) =>
    series
      .filter((item) => item.axis === axis)
      .flatMap((item) => item.data.map((point) => point.value));

  const leftDomain = getValueDomain(valuesOn("left"), scale);
  const rightDomain = getValueDomain(valuesOn("right"), scale);
  const hasRightAxis = series.some((item) => item.axis === "right");

  const bars = series.filter((item) => item.kind === "bar");
  // One subgroup slot per bar series, shared by both axes so left- and
  // right-axis bars sit side by side within a date.
  const barGroup = group({
    scale: scaleBand<string>()
      .domain(bars.map((item) => String(item.index)))
      .paddingInner(0.08),
  });

  const barMarks = bars.map((item) => {
    const options = {
      id: `bars-${item.index}`,
      x: "isoDate",
      y: "value",
      z: (row: TimeseriesNewRow) => String(row.seriesIndex),
      key: (row: TimeseriesNewRow) => `${row.seriesIndex}:${row.isoDate}`,
      fill: seriesColour(item, brandTheme.mode),
      fillOpacity: item.muted ? MUTED_OPACITY : 1,
      layout: barGroup,
      radius: { end: 2 },
    } as const;

    return item.axis === "right"
      ? barY(rowsOf(item), { ...options, yScale: "right" })
      : barY(rowsOf(item), options);
  });

  const lineMarks = series
    .filter((item) => item.kind === "line")
    .map((item) => {
      const options = {
        id: `line-${item.index}`,
        x: "isoDate",
        y: "value",
        key: (row: TimeseriesNewRow) => `${row.seriesIndex}:${row.isoDate}`,
        stroke: seriesColour(item, brandTheme.mode),
        strokeOpacity: item.muted ? MUTED_OPACITY : 1,
        strokeWidth: 2.5,
      } as const;

      return item.axis === "right"
        ? lineY(rowsOf(item), { ...options, yScale: "right" })
        : lineY(rowsOf(item), options);
    });

  const markerRows: MarkerRow[] = markers.map((marker) => {
    const labelled = marker.labels.find((label) => label.placement) ?? {
      color: undefined,
      lineType: undefined,
    };

    return {
      isoDate: marker.isoDate,
      color: labelled.color ?? marker.color,
      strokeWidth: marker.width ?? 1.5,
      strokeDasharray: lineTypeToDashArray(
        (labelled.lineType ?? marker.lineType ?? "solid") as MarkerLineType,
      ),
      description: marker.description,
    };
  });

  const markerLabels = (placement: "top" | "bottom"): MarkerLabelRow[] =>
    markers.flatMap((marker) =>
      marker.labels
        .filter((label) => label.placement === placement)
        .map((label) => ({
          isoDate: marker.isoDate,
          name: label.name,
          color: label.color ?? marker.color,
        })),
    );

  const muted = brandTheme.theme.muted ?? brandTheme.theme.foreground;
  const labelledDates = new Map(
    xAxisLabels.map((label) => [label.isoDate, label.label]),
  );

  return defineChart({
    theme: brandTheme.theme,
    marks: [
      ...markerRows.map((row) =>
        ruleX([row], {
          id: `marker-${row.isoDate}`,
          x: "isoDate",
          stroke: row.color ?? muted,
          strokeWidth: row.strokeWidth,
          strokeDasharray: row.strokeDasharray,
        }),
      ),
      // Rules have no hover points; an invisible dot per marker puts its
      // description in the tooltip for that date.
      dot(markerRows, {
        id: "marker-anchors",
        x: "isoDate",
        y: () => leftDomain[1],
        key: "isoDate",
        r: 1,
        fillOpacity: 0,
      }),
      ...(displayLinesInFrontOfBars
        ? [...barMarks, ...lineMarks]
        : [...lineMarks, ...barMarks]),
      text(markerLabels("top"), {
        id: "marker-labels-top",
        x: "isoDate",
        y: () => leftDomain[1],
        text: "name",
        fill: (row) => row.color ?? muted,
        fontSize: 12,
        dy: -8,
      }),
      text(markerLabels("bottom"), {
        id: "marker-labels-bottom",
        x: "isoDate",
        y: () => leftDomain[0],
        text: "name",
        fill: (row) => row.color ?? muted,
        fontSize: 12,
        dy: -8,
      }),
      crosshair({ x: { band: true }, y: false }),
    ],
    scales: {
      x: {
        scale: scaleBand<string>()
          .domain(timeValues)
          .paddingInner(padding.paddingInner)
          .paddingOuter(padding.paddingOuter),
        axis: {
          ticks: { size: 0, format: formatTimeValue },
          tickLabels: { thin: true },
        },
      },
      // Optional row of labels above the plot, e.g. buyer research stages.
      top: {
        channel: "x",
        side: "top",
        scale: scaleBand<string>()
          .domain(timeValues)
          .paddingInner(padding.paddingInner)
          .paddingOuter(padding.paddingOuter),
        axis: labelledDates.size
          ? {
              line: false,
              ticks: {
                size: 0,
                values: timeValues.filter((date) => labelledDates.has(date)),
                format: (date: string) => labelledDates.get(date) ?? "",
              },
              tickLabels: { thin: true, fontWeight: 600 },
            }
          : false,
      },
      y: {
        scale: valueScale(leftDomain, scale),
        grid: true,
        axis: valueAxis(scale, leftAxisLabel, skipDecimals),
      },
      // Always declared so the definition has one shape; hidden when unused.
      right: {
        channel: "y",
        side: "right",
        scale: valueScale(rightDomain, scale),
        axis: hasRightAxis
          ? valueAxis(scale, rightAxisLabel, skipDecimals)
          : false,
      },
    },
  });
};
